import { getDefaultStore } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { getLocalStorage } from 'src/utils/storage'

/**
 * Duplicating attemptParseJSON in this file to prevent
 * unnecessary lodash-es import when config being read from
 * Firebase service worker
 * @param str
 * @returns
 */
const attemptParseJSON = (str?: string | null) => {
  if (!str) return
  try {
    return JSON.parse(str)
  } catch (e) {}
}

export type AppConfig = typeof config
export const config = {
  apiUrl: import.meta.env.VITE_API_URL || 'https://api.pixai.art',
  gatewayUrl: import.meta.env.VITE_GATEWAY_URL || 'wss://gw.pixai.art',
  rapiUrl: import.meta.env.VITE_RAPI_URL || 'https://rapi.pixai.art',
  kotoneApiUrl:
    import.meta.env.VITE_KOTONE_API_URL || 'https://kotone-api.pixai.art',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  amplitudeApiKey: import.meta.env.VITE_AMPLITUDE_API_KEY || '',
  amplitudeExperimentsServerUrl: 'https://experiments.pixai.art',
  amplitudeExperimentsFlagsServerUrl: 'https://flab.pixai.art',
  amplitudeExperimentDeploymentKey:
    import.meta.env.VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY || '',
  amplitudeServerUrl: 'https://t.pixai.art/',
  loggerServiceUrl: 'https://logger-api.mori.mewtant.co',
  getway: {
    ak: import.meta.env.VITE_GETWAY_AK,
    sk: import.meta.env.VITE_GETWAY_SK,
  },
  appVersion: import.meta.env.VITE_APP_VERSION,
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || '',
  commitHash: import.meta.env.VITE_COMMIT_HASH || '',
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '',
  shareUrl: import.meta.env.VITE_SHARE_URL || 'https://s.pixai.art',
  adbiUrl: import.meta.env.VITE_ADBI_URL || '',
  iosAppStoreLink:
    'https://apps.apple.com/app/pixai-ai-anime-art-generator/id6474528911',
  playStoreLink:
    'https://play.google.com/store/apps/details?id=io.mewtant.pixaiart&listing=pixaiweb-app',
  apkReleaseLink: 'https://cdn.pixai.art/android_apk/app_release_latest.apk',
  helpCenterUrl:
    import.meta.env.VITE_HELP_CENTER_URL || 'https://support.pixai.art/',
  youtubeChannelUrl:
    import.meta.env.VITE_YOUTUBE_CHANNEL_URL ||
    'https://www.youtube.com/@PixaiART',

  // ComfyUI library URL
  comfyAppLibUrl:
    import.meta.env.VITE_COMFY_APP_LIB_URL ||
    'https://cdn.pixai.art/lib/comfyui/index.js',

  // Firebase
  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY || '',
  },

  // Turnstile
  turnstile: {
    invisibleSiteKey:
      import.meta.env.VITE_TURNSTILE_INVISIBLE_SITE_KEY ||
      '1x00000000000000000000BB',
    nonInteractiveSiteKey:
      import.meta.env.VITE_TURNSTILE_NON_INTERACTIVE_SITE_KEY ||
      '1x00000000000000000000AA',
    managedSiteKey:
      import.meta.env.VITE_TURNSTILE_MANAGED_SITE_KEY ||
      '1x00000000000000000000AA',
  },

  google: {
    oneTapClientId: import.meta.env.VITE_GOOGLE_ONE_TAP_CLIENT_ID || '',
  },

  intercomAppId: import.meta.env.VITE_INTERCOM_APP_ID || 'kq7t2r83',
  siftJsKeyProd:
    import.meta.env.VITE_SIFT_JS_KEY_PROD ||
    import.meta.env.VITE_SIFT_JS_KEY ||
    '348e8126a3',
  siftJsKeySandbox:
    import.meta.env.VITE_SIFT_JS_KEY_SANDBOX ||
    import.meta.env.VITE_SIFT_JS_KEY ||
    '348e8126a3',
}

/**
 * Runtime values that are injected into window at build time.
 * Kept separate from config to avoid SSR issues.
 */
export const runtimeConfig = {
  get buildTimestamp() {
    if (typeof window !== 'undefined') return window.__build_timestamp || ''
    return ''
  },
  get commitHash() {
    if (typeof window !== 'undefined') return window.__commit_hash || ''
    return ''
  },
  get isSSR() {
    if (typeof window !== 'undefined')
      return window.__reactRouterContext?.ssr === true
    if (typeof self !== 'undefined') return false // Service Worker
    return true // Node.js server
  },
}

export const getApiUrl = (): string =>
  attemptParseJSON(getLocalStorage('api-base-url')) || config.apiUrl

export const getWebsocketUrl = (): string =>
  attemptParseJSON(getLocalStorage('gateway-base-url')) || config.gatewayUrl

export const getKotoneApiUrl = (): string =>
  attemptParseJSON(getLocalStorage('kotone-base-url')) || config.kotoneApiUrl

export const getAppEnv = () => {
  return {
    get isProd() {
      if (typeof window === 'undefined') return config.isProd
      return config.isProd && !window.location.hostname.endsWith('pages.dev')
    },
    get isDev() {
      return !this.isProd
    },
  }
}

export const getApiEnv = () => {
  const apiUrl = getApiUrl()
  return {
    get isProd() {
      return apiUrl.includes('api.pixai.art')
    },
    get isTest() {
      return apiUrl.includes('ring.mewtant.co') && apiUrl.includes('papi')
    },
    get isStaging() {
      return apiUrl.includes('pixai.art') && apiUrl.includes('pai-api')
    },
    get isLocal() {
      return !this.isProd && !this.isTest && !this.isStaging
    },
    get name() {
      if (this.isProd) return 'prod'
      if (this.isTest) return 'test'
      if (this.isStaging) return 'staging'
      return 'local'
    },
  }
}

export const enableLoggerServiceAtom = atomWithStorage(
  'debug::enable-logger-service',
  false,
  undefined,
  { getOnInit: true },
)

export const shouldUseLoggerService = (): boolean => {
  const enableLoggerService = getDefaultStore().get(enableLoggerServiceAtom)
  if (enableLoggerService) return true
  return config.isDev && !config.amplitudeApiKey
}

export const getAmplitudeServerUrl = (): string => {
  return shouldUseLoggerService()
    ? config.loggerServiceUrl
    : config.amplitudeServerUrl
}

export const getAmplitudeApiKey = (): string => {
  return shouldUseLoggerService()
    ? 'mock-amplitude-api-key'
    : config.amplitudeApiKey
}
